import type { Json } from '../supabase/database.types'
import type { Role } from '../../types/domain'
import type { BuyerChange } from '../../features/compras/types'
import type { HistoricoRow, RequisicaoDraft, RequisicaoItemRow, RequisicaoRow } from '../../features/requisicoes/types'

export const OFFLINE_DB_NAME = 'RequisicoesPWAOfflineDB'
export const OFFLINE_DB_VERSION = 3

export type ConnectivityState = 'ONLINE' | 'OFFLINE' | 'DEGRADED'
export type OutboxStatus = 'PENDING' | 'PROCESSING' | 'NEEDS_ATTENTION'
export type OfflineOperationType =
  | 'CREATE_REQUISICAO'
  | 'SAVE_REQUISICAO'
  | 'SAVE_COMPRA'
  | 'BLOCK_REQUISICAO'
  | 'DELETE_REQUISICAO'
  | 'CREATE_ITEM_REQUEST'

export interface CacheMetadata {
  source: 'server' | 'cache'
  cachedAt: string
  serverVersion: number
  pending?: boolean
  localOnly?: boolean
  locallyDeleted?: boolean
}

export interface CachedRequisition {
  key: string
  userId: string
  row: RequisicaoRow
  cachedAt: string
  serverVersion: number
  pending: boolean
  localOnly: boolean
  locallyDeleted: boolean
  listItems: Array<{
    id: string
    codigo: string | null
    descricao: string
    prioridade: string | null
    status_item: string
    quantidade_comprada: number | null
    fornecedor: string | null
  }>
}

export interface CachedItem {
  key: string
  userId: string
  requisicaoId: string
  row: RequisicaoItemRow
  cachedAt: string
  serverVersion: number
  pending: boolean
  locallyDeleted: boolean
}

export interface CachedHistory {
  key: string
  userId: string
  requisicaoId: string
  row: HistoricoRow
  cachedAt: string
}

export interface CachedCatalogItem {
  key: string
  userId: string
  row: import('../supabase/database.types').Tables<'itens_catalogo'>
  cachedAt: string
  serverVersion: number
}

export interface SyncMetadata {
  key: string
  userId: string
  kind: 'last_sync' | 'profile'
  value: Json
  updatedAt: string
}

export type OutboxPayload =
  | { type: 'CREATE_REQUISICAO'; localRequisicaoId: string; draft: RequisicaoDraft }
  | { type: 'SAVE_REQUISICAO'; draft: RequisicaoDraft }
  | { type: 'SAVE_COMPRA'; requisicaoId: string; changes: BuyerChange[] }
  | { type: 'BLOCK_REQUISICAO'; requisicaoId: string; expectedVersion: number }
  | { type: 'DELETE_REQUISICAO'; requisicaoId: string; expectedVersion: number }
  | {
      type: 'CREATE_ITEM_REQUEST'
      request: {
        tipo: string; codigo: string; descricao: string; grade_1: string; grade_2: string;
        grade_3: string; unidade: string; categoria: string; motivo: string;
        requisicaoId: string | null; requisicaoItemId: string | null
      }
    }

export interface OutboxOperation {
  operationId: string
  entityType: 'REQUISICAO' | 'COMPRA' | 'ITEM_REQUEST'
  entityId: string
  operationType: OfflineOperationType
  payload: OutboxPayload
  baseVersion: number | null
  createdAt: string
  attempts: number
  lastAttemptAt: string | null
  lastError: string | null
  status: OutboxStatus
  userId: string
  role: Role
  dependsOn: string[]
  nextAttemptAt: string | null
}

export interface SyncResult {
  processed: number
  pending: number
  needsAttention: number
}
